import React, { useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import { NamedLink } from '../../../../components';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import landingPageIcons from '../landingPageIcons';

import css from './SectionHeroBanner.module.css';

/**
 * @typedef {Object} HeroStat
 * @property {string} value the big number, e.g. "100+"
 * @property {string} label the caption below it, e.g. "Verified service providers"
 */

/**
 * @typedef {Object} HeroSearch
 * @property {string?} placeholder placeholder for the keyword input
 * @property {string?} buttonLabel label on the submit button
 * @property {Array<{label: string, keywords: string?}>?} chips shortcut pills below the input.
 *   Each links to SearchPage with `keywords` (falling back to the chip's own label).
 */

/**
 * Homepage hero: a photographic background with a dark scrim, a heading + description,
 * two call-to-action buttons, a stats panel, and a keyword search panel.
 *
 * The stats panel and the search panel deliberately interlock: the stats panel is
 * right-aligned with rounded top corners and the search panel below it squares off its
 * top-right corner, so the two read as one stepped white surface (see the .statsPanel /
 * .searchPanel rules).
 *
 * Search submits to SearchPage via the same `createResourceLocatorString` route helper the
 * PageBuilder SearchCTA primitive uses. It is NOT the SearchCTA component itself: SearchCTA
 * renders whatever filter set the hosted config enables (categories / location / dates) in
 * its own grid, whereas this design is a single keyword field with a fixed-width button.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {'heroBanner'} props.sectionType
 * @param {Object?} props.badge optional eyebrow badge: { icon: keyof landingPageIcons, text: string }
 * @param {Object?} props.title heading config for the non-highlighted part of the H1 (Field data)
 * @param {Object?} props.titleHighlight optional highlighted tail of the H1 (Field data)
 * @param {Object?} props.description description text (Field data)
 * @param {Object?} props.callToAction primary (filled) button (Field data)
 * @param {Object?} props.secondaryCallToAction secondary (outlined) button (Field data)
 * @param {Array<HeroStat>?} props.stats numbers shown in the right-hand panel
 * @param {HeroSearch?} props.search keyword search panel; omit to hide it entirely
 * @param {Object?} props.appearance
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const SectionHeroBanner = props => {
  const {
    sectionId,
    className,
    rootClassName,
    badge,
    title,
    titleHighlight,
    description,
    callToAction,
    secondaryCallToAction,
    stats = [],
    search,
    appearance,
    options,
  } = props;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const [keywords, setKeywords] = useState('');

  const fieldOptions = { fieldComponents: options?.fieldComponents };
  const hasHeader = hasDataInFields(
    [title, titleHighlight, description, callToAction, secondaryCallToAction],
    fieldOptions
  );
  const badgeIcon = badge?.icon && landingPageIcons[badge.icon];
  const searchIcon = landingPageIcons.search;

  const onSubmitSearch = e => {
    e.preventDefault();
    const trimmed = keywords.trim();
    const queryParams = trimmed ? { keywords: trimmed } : {};
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  };

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(rootClassName || css.root, className)}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.scrim} />

      <div className={css.inner}>
        {hasHeader ? (
          <div className={css.content}>
            {badge?.text ? (
              <div className={css.badge}>
                {badgeIcon ? <img className={css.badgeIcon} src={badgeIcon} alt="" /> : null}
                <span>{badge.text}</span>
              </div>
            ) : null}

            <h1 className={css.title}>
              <Field data={title} options={fieldOptions} />
              {titleHighlight ? (
                <>
                  {' '}
                  <span className={css.titleHighlight}>
                    <Field data={titleHighlight} options={fieldOptions} />
                  </span>
                </>
              ) : null}
            </h1>

            <Field data={description} className={css.description} options={fieldOptions} />
          </div>
        ) : null}

        {/* CTAs and the stats panel share a row on wide viewports - the buttons sit bottom-left
            and the stats panel bottom-right, directly on top of the search panel. */}
        <div className={css.actions}>
          <div className={css.ctaRow}>
            <Field data={callToAction} className={css.primaryCta} options={fieldOptions} />
            <Field
              data={secondaryCallToAction}
              className={css.secondaryCta}
              options={fieldOptions}
            />
          </div>

          {stats.length > 0 ? (
            <div className={css.statsPanel}>
              {stats.map((stat, index) => (
                <div key={stat.value || index} className={css.stat}>
                  <span className={css.statValue}>{stat.value}</span>
                  <span className={css.statLabel}>{stat.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {search ? (
          <div className={css.searchPanel}>
            <form className={css.searchForm} onSubmit={onSubmitSearch} role="search">
              <input
                className={css.searchInput}
                type="text"
                value={keywords}
                onChange={e => setKeywords(e.target.value)}
                placeholder={search.placeholder}
                aria-label={search.placeholder || search.buttonLabel}
              />
              <button className={css.searchButton} type="submit">
                <img className={css.searchButtonIcon} src={searchIcon} alt="" />
                <span>{search.buttonLabel}</span>
              </button>
            </form>

            {search.chips?.length > 0 ? (
              <div className={css.chipRow}>
                {search.chips.map(chip => (
                  <NamedLink
                    key={chip.label}
                    className={css.chip}
                    name="SearchPage"
                    to={{ search: `?keywords=${encodeURIComponent(chip.keywords || chip.label)}` }}
                  >
                    {chip.label}
                  </NamedLink>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </SectionContainer>
  );
};

export default SectionHeroBanner;
